import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { TierId } from "@/lib/figVerifyTiers";

const PAID_TIERS = new Set<string>(["collector", "seller", "pro_seller"]);

export interface ScanBalance {
  freeRemaining: number;
  scanCredits: number;
  trialActive: boolean;
  subscriptionTier: "none" | "trial" | TierId;
  subscriptionStatus: string | null;
  subscriptionMonthlyCap: number;
  subscriptionRenewsAt: string | null;
  totalRemaining: number;
  hasAccess: boolean;
}

export function useScanBalance() {
  const { user } = useAuth();
  const [balance, setBalance] = useState<ScanBalance | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setBalance(null); setLoading(false); return; }
    const { data } = await supabase
      .from("profiles")
      .select("free_scans_remaining, scan_credits, trial_active, subscription_tier, subscription_status, subscription_monthly_cap, subscription_renews_at")
      .eq("id", user.id)
      .maybeSingle();
    const row = data as unknown as {
      free_scans_remaining?: number;
      scan_credits?: number;
      trial_active?: boolean;
      subscription_tier?: string;
      subscription_status?: string | null;
      subscription_monthly_cap?: number;
      subscription_renews_at?: string | null;
    } | null;
    const freeRemaining = row?.free_scans_remaining ?? 0;
    const scanCredits = row?.scan_credits ?? 0;
    const trialActive = row?.trial_active ?? false;
    const tier = (row?.subscription_tier ?? "none") as ScanBalance["subscriptionTier"];
    const status = row?.subscription_status ?? null;
    const cap = row?.subscription_monthly_cap ?? 0;
    const subActive = PAID_TIERS.has(tier) && (status === "active" || status === "trialing");
    const totalRemaining = freeRemaining + scanCredits + (subActive ? cap : 0);
    setBalance({
      freeRemaining,
      scanCredits,
      trialActive,
      subscriptionTier: tier,
      subscriptionStatus: status,
      subscriptionMonthlyCap: cap,
      subscriptionRenewsAt: row?.subscription_renews_at ?? null,
      totalRemaining,
      hasAccess: trialActive || totalRemaining > 0 || (subActive && cap > 0),
    });
    setLoading(false);
  }, [user]);

  useEffect(() => { void refresh(); }, [refresh]);

  return { balance, loading, refresh };
}
